import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
    Search, MapPin, ArrowRight, ArrowUpRight,
    Shield, Wallet, Zap, Star, Clock,
} from 'lucide-react';

function normaliseJob(j) {
    const today = new Date().toDateString();
    return {
        id: j.id,
        title: j.title,
        pay: `€${j.payment?.toFixed(0) ?? '?'}`,
        duration: j.duration || '',
        area: j.address || '',
        cat: j.tags?.split(',')[0]?.trim() || '',
        urgent: j.dateTime ? new Date(j.dateTime).toDateString() === today : false,
    };
}

const POPULAR = ['Cleaning', 'Moving', 'Bartending', 'Dog walking', 'Handyman', 'Babysitting', 'Gardening', 'Delivery'];

const CATEGORIES = [
    { name: 'Cleaning',    emoji: '✨', count: 84, color: '#1ea683' },
    { name: 'Moving',      emoji: '📦', count: 41, color: '#e07b54' },
    { name: 'Pets',        emoji: '🐾', count: 38, color: '#d4aa53' },
    { name: 'Delivery',    emoji: '🚚', count: 56, color: '#5b8dee' },
    { name: 'Handy work',  emoji: '🔧', count: 29, color: '#b35ea3' },
    { name: 'Hospitality', emoji: '☕', count: 47, color: '#4caf78' },
    { name: 'Childcare',   emoji: '👶', count: 17, color: '#5bb0d4' },
    { name: 'Other',       emoji: '⭐', count: 23, color: '#6b7570' },
];

const STEPS = [
    { n: '01', title: 'Post or browse',       body: 'Describe what you need in two lines, or scroll a feed of jobs near you. No long forms.' },
    { n: '02', title: 'Match in minutes',     body: 'Posters get applicants in around 11 minutes. Workers get a yes/no the same hour.' },
    { n: '03', title: 'Do the work, get paid', body: 'Pay is held the moment a job is accepted, and released the second it\'s done. Zero chase.' },
];

const PERSONAS = [
    {
        tag: 'For workers',
        title: 'Interim puts you in control.',
        body: 'Whether you\'re a student earning extra cash, a freelancer between gigs, or just need a quick helping hand.',
        stat: '4.9★', statLabel: 'Avg worker rating', cta: 'Find work today', link: '/get-job',
    },
    {
        tag: 'For posters',
        title: 'You set the price.',
        body: 'Need a leak fixed, a quick tidy-up, or a last-minute babysitter? Set your price — local helpers will take the job.',
        stat: '11 min', statLabel: 'Avg first reply', cta: 'Post a job', link: '/create-job',
    },
    {
        tag: 'For businesses',
        title: 'Cover a shift before close.',
        body: 'Café needing a weekend barista? Shop short on a salesperson? Post the shift, see verified locals, fill it within the hour.',
        stat: '92%', statLabel: 'Shifts filled in <2hrs', cta: 'Hire for a shift', link: '/create-job',
    },
];

const TESTIMONIALS = [
    { quote: 'Posted a job at 9.04am. Had a guy fixing the boiler by 10.15. Cheaper than my regular plumber.',         name: 'Marcus L.',  role: 'Dublin · poster',        rating: 5 },
    { quote: 'I do two evening shifts a week through Interim and it covers my rent. Same-day payout is the killer feature.', name: 'Priya S.',   role: 'Rathmines · worker',     rating: 5 },
    { quote: 'We\'ve stopped using agencies for last-minute cover. Faster, half the price, and the staff are great.',   name: 'Olu K.',     role: 'Café owner, Smithfield',  rating: 5 },
];

/* ─── Sub-components ─── */
function JobRow({ job, delay = 0 }) {
    return (
        <div className="ix-job-row" style={{ animationDelay: `${delay}ms` }}>
            <div>
                <div className="ix-job-cat">{job.cat}</div>
                <div className="ix-job-title">{job.title}</div>
                <div className="ix-job-meta">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={12} /> {job.area}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={12} /> {job.duration}
                    </span>
                    {job.urgent && (
                        <span className="ix-urgent-tag">
                            <Zap size={10} /> Today
                        </span>
                    )}
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                <div className="ix-pay-amount">{job.pay}</div>
                <Link to="/get-job" className="ix-btn ix-btn-outline ix-btn-sm">Apply</Link>
            </div>
        </div>
    );
}

function TrustItem({ icon: Icon, title, body }) {
    return (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span className="ix-trust-icon"><Icon size={20} /></span>
            <div>
                <div style={{ fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink-950)', marginBottom: '4px' }}>{title}</div>
                <p className="ix-body" style={{ margin: 0 }}>{body}</p>
            </div>
        </div>
    );
}

/* ─── Main component ─── */
const Home = ({ user }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [liveJobs, setLiveJobs] = useState([]);
    const [feedLoading, setFeedLoading] = useState(true);
    const [jobCount, setJobCount] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/popular`)
            .then(r => setLiveJobs((r.data ?? []).map(normaliseJob)))
            .catch(() => {})
            .finally(() => setFeedLoading(false));
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/count`)
            .then(r => setJobCount(r.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        axios.get('https://countriesnow.space/api/v0.1/countries/iso')
            .then(r => setCountries((r.data?.data ?? []).map(c => c.name)))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!filterCountry) { setCities([]); setFilterCity(''); return; }
        setCitiesLoading(true);
        axios.post('https://countriesnow.space/api/v0.1/countries/cities', { country: filterCountry })
            .then(r => setCities(r.data?.data ?? []))
            .catch(() => setCities([]))
            .finally(() => setCitiesLoading(false));
    }, [filterCountry]);

    /* Pre-fill from user profile */
    useEffect(() => {
        if (user?.country && !filterCountry) setFilterCountry(user.country);
        if (user?.city && !filterCity) setFilterCity(user.city);
    }, [user?.country, user?.city]);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (searchTerm.trim()) params.set('search', searchTerm.trim());
        if (filterCity) params.set('location', filterCity);
        navigate(`/get-job${params.toString() ? `?${params}` : ''}`);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    return (
        <main style={{ background: 'var(--paper)' }}>

            {/* ═══ HERO ═══ */}
            <section
                className="ix-section"
                style={{ paddingTop: '64px', paddingBottom: '48px', position: 'relative', overflow: 'hidden' }}
            >
                {/* Background gradient blobs */}
                <div style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
                    background: 'radial-gradient(60% 50% at 80% 0%, color-mix(in srgb,var(--teal-500) 10%,transparent) 0%,transparent 60%), radial-gradient(50% 40% at 0% 20%, color-mix(in srgb,#d4aa53 8%,transparent) 0%,transparent 60%)',
                }} />

                <div className="ix-container" style={{ position: 'relative', zIndex: 1 }}>

                    {/* Live chip */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                        <span className="chip live" style={{ marginBottom: '28px', display: 'inline-flex' }}>
                            {jobCount !== null ? `${jobCount.toLocaleString()} job${jobCount !== 1 ? 's' : ''} live now` : 'Jobs live now'}
                        </span>
                    </motion.div>

                    {/* Headline */}
                    <motion.h1
                        className="h-display"
                        style={{ marginBottom: '24px' }}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        One day.<br />One job. <span className="serif">Done.</span>
                    </motion.h1>

                    {/* Lede */}
                    <motion.p
                        className="lede"
                        style={{ marginBottom: '36px', maxWidth: '56ch' }}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        Interim connects people with quick, paid, single-day work — from a two-hour move to a weekend shift behind the bar.
                    </motion.p>

                    {/* Search bar */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                        <div className="ix-searchbar">
                            <div className="ix-searchbar-field">
                                <Search size={18} color="var(--ink-400)" style={{ flexShrink: 0 }} />
                                <input
                                    placeholder="What needs doing? e.g. moving sofa, weekend bar shift…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div className="ix-searchbar-divider" />
                            <div className="ix-searchbar-field" style={{ flex: '0 0 auto', maxWidth: '160px' }}>
                                <MapPin size={18} color="var(--ink-400)" style={{ flexShrink: 0 }} />
                                <select
                                    value={filterCountry}
                                    onChange={e => { setFilterCountry(e.target.value); setFilterCity(''); }}
                                    style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '15px', color: filterCountry ? 'var(--ink-900)' : 'var(--ink-400)', cursor: 'pointer' }}
                                >
                                    <option value="">Country</option>
                                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="ix-searchbar-divider" />
                            <div className="ix-searchbar-field" style={{ flex: '0 0 auto', maxWidth: '160px' }}>
                                <select
                                    value={filterCity}
                                    onChange={e => setFilterCity(e.target.value)}
                                    disabled={!filterCountry || citiesLoading}
                                    style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '15px', color: filterCity ? 'var(--ink-900)' : 'var(--ink-400)', cursor: filterCountry ? 'pointer' : 'default' }}
                                >
                                    <option value="">
                                        {!filterCountry ? 'City' : citiesLoading ? 'Loading…' : 'City'}
                                    </option>
                                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <button
                                className="ix-btn ix-btn-primary"
                                style={{ padding: '13px 22px', marginLeft: '4px' }}
                                onClick={handleSearch}
                            >
                                <Search size={16} /> Find work
                            </button>
                        </div>

                        {/* Popular tags */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '20px', flexWrap: 'wrap' }}>
                            <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>Popular</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {POPULAR.map((tag) => (
                                    <button
                                        key={tag}
                                        className="ix-popular-tag"
                                        onClick={() => navigate(`/get-job?search=${encodeURIComponent(tag)}`)}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Live job feed preview */}
                    <motion.div
                        className="ix-hero-preview"
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
                    >
                        <div className="ix-hero-preview-head">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span className="chip live">Live feed</span>
                            </div>
                            <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-400)' }}>Most popular</span>
                        </div>

                        {feedLoading && (
                            <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-400)' }}>
                                Loading…
                            </div>
                        )}
                        {!feedLoading && liveJobs.length === 0 && (
                            <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-400)' }}>
                                No live jobs right now — check back soon.
                            </div>
                        )}
                        {liveJobs.map((job, i) => (
                            <JobRow key={job.id} job={job} delay={i * 80} />
                        ))}

                        <div className="ix-hero-preview-foot">
                            <span className="mono">Live data</span>
                            <Link to="/get-job" className="ix-link-arrow">
                                See all jobs <ArrowUpRight size={13} />
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ═══ HOW IT WORKS ═══ */}
            <section className="ix-section" style={{ background: 'var(--paper-2)' }}>
                <div className="ix-container">
                    <div className="ix-section-head">
                        <div>
                            <span className="eyebrow" style={{ display: 'block', marginBottom: '14px' }}>How it works</span>
                            <h2 className="h-section">From <span className="serif">idea</span> to done in an afternoon.</h2>
                        </div>
                    </div>

                    <div className="ix-steps-grid">
                        {STEPS.map((step, i) => (
                            <div key={step.n} className="ix-step">
                                <div className="ix-step-n">{step.n}</div>
                                <h3 className="h-card" style={{ marginBottom: '12px', color: 'var(--ink-950)' }}>{step.title}</h3>
                                <p className="ix-body">{step.body}</p>
                                {i < STEPS.length - 1 && <div className="ix-step-line" />}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══ PERSONAS ═══ */}
            <section className="ix-section" style={{ background: 'var(--white)' }}>
                <div className="ix-container">
                    <div className="ix-section-head">
                        <div>
                            <span className="eyebrow" style={{ display: 'block', marginBottom: '14px' }}>Who is Interim for</span>
                            <h2 className="h-section">Built for <span className="serif">three</span> kinds of day.</h2>
                        </div>
                    </div>

                    <div className="ix-persona-grid">
                        {PERSONAS.map((p) => (
                            <article key={p.tag} className="ix-persona-card">
                                <div className="ix-persona-tag">{p.tag}</div>
                                <h3 className="h-card" style={{ color: 'var(--ink-950)' }}>{p.title}</h3>
                                <p className="ix-body">{p.body}</p>
                                <div style={{
                                    display: 'flex', alignItems: 'baseline', gap: '12px',
                                    paddingTop: '20px', marginTop: 'auto',
                                    borderTop: '1px solid var(--ink-200)',
                                }}>
                                    <div className="ix-persona-stat-n">{p.stat}</div>
                                    <div className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>{p.statLabel}</div>
                                </div>
                                <Link to={p.link} className="ix-link-arrow" style={{ marginTop: '4px' }}>
                                    {p.cta} <ArrowRight size={14} />
                                </Link>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══ TRUST STRIP ═══ */}
            <section style={{ paddingBlock: '64px' }}>
                <div className="ix-container">
                    <div className="ix-trustbar-grid">
                        <TrustItem
                            icon={Shield}
                            title="See who's verified"
                            body="Some workers choose to verify their ID — you'll see who has, right next to their application."
                        />
                        <TrustItem
                            icon={Wallet}
                            title="Same-day payout"
                            body="Money lands in your account the day the job is done."
                        />
                        <TrustItem
                            icon={Zap}
                            title="No long-term commitment"
                            body="One day in, one day out. No contracts, no subscription."
                        />
                    </div>
                </div>
            </section>

            {/* ═══ TESTIMONIALS ═══ */}
            <section className="ix-section" style={{ background: 'var(--paper-2)' }}>
                <div className="ix-container">
                    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                        <span className="eyebrow" style={{ display: 'block', marginBottom: '14px' }}>From real Interim users</span>
                        <h2 className="h-section">Short jobs. <span className="serif">Long</span> recommendations.</h2>
                    </div>

                    <div className="ix-testi-grid">
                        {TESTIMONIALS.map((t, i) => (
                            <figure key={i} className="ix-testi">
                                <div className="ix-testi-stars" style={{ display: 'flex', gap: '2px' }}>
                                    {Array.from({ length: t.rating }).map((_, k) => (
                                        <Star key={k} size={13} fill="currentColor" strokeWidth={0} />
                                    ))}
                                </div>
                                <blockquote className="ix-testi-quote">"{t.quote}"</blockquote>
                                <figcaption style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    marginTop: 'auto', paddingTop: '18px',
                                    borderTop: '1px solid var(--ink-200)',
                                }}>
                                    <div className="ix-testi-avatar">{t.name[0]}</div>
                                    <div>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink-950)' }}>{t.name}</div>
                                        <div className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)', marginTop: '2px' }}>{t.role}</div>
                                    </div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══ FINAL CTA ═══ */}
            <section className="ix-section">
                <div className="ix-container">
                    <div className="ix-cta-card">
                        <div className="ix-cta-grain" />
                        <div style={{ position: 'relative', maxWidth: '720px' }}>
                            <span className="eyebrow" style={{ color: 'var(--teal-300)' }}>Get going</span>
                            <h2 style={{
                                color: '#fff', margin: '16px 0 32px',
                                fontFamily: 'var(--font-sans)',
                                fontSize: 'clamp(32px, 4.5vw, 60px)',
                                fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.025em',
                            }}>
                                Your next gig — or your next{' '}
                                <span style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontWeight: 400 }}>helper</span>
                                {' '}— is one tap away.
                            </h2>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <Link
                                    to="/get-job"
                                    className="ix-btn ix-btn-lg"
                                    style={{ background: '#fff', color: 'var(--ink-950)' }}
                                >
                                    Find work <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/create-job"
                                    className="ix-btn ix-btn-lg"
                                    style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.22)' }}
                                >
                                    Post a job
                                </Link>
                            </div>
                            <div className="mono" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginTop: '28px' }}>
                                No fees on your first job · Cancel anytime · 4.9★ from 12k reviews
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
